import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Chip,
  Stack,
  Card,
  CardContent,
  Fade,
  Grow,
  InputAdornment,
  Dialog,
  AppBar,
  Toolbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  ListSubheader,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import useVoucher from '../../hooks/useVoucher';
import useVoucherProduct from '../../hooks/useVoucherProduct';
import useResourceVoucher from '../../hooks/useResourceVoucher';
import useResourceVoucherProduct from '../../hooks/useResourceVoucherProduct';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import { isValidEmail } from '../../util/validation';
import { inlinePdfUrl } from '../../util/pdfUrl';

const steps = ['Gutschein konfigurieren', 'Zahlungseingang bestätigen', 'Abschluss'];

function ConfirmBankTransferVoucherPayment() {
  const theme = useTheme();
  const authCtx = useContext(AuthContext);
  const {
    sellOfflineVoucher,
    sendVoucherToEmail,
    getAllVouchers,
    getVoucherDownloadURL,
    getVouchersZipURL,
  } = useVoucher();
  const { getAllVoucherProducts, isLoading: isLoadingProducts } = useVoucherProduct();
  const { sellOfflineResourceVoucher } = useResourceVoucher();
  const { getAllResourceVoucherProducts, isLoading: isLoadingResourceProducts } = useResourceVoucherProduct();

  const [activeStep, setActiveStep] = useState(0);
  const [voucherProducts, setVoucherProducts] = useState([]);
  const [resourceVoucherProducts, setResourceVoucherProducts] = useState([]);
  const [selectedVoucherProduct, setSelectedVoucherProduct] = useState('');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [selectedSuggestedValue, setSelectedSuggestedValue] = useState('');
  const [voucherValue, setVoucherValue] = useState('');
  const quantityRef = useRef(null);
  const [draftQuantity, setDraftQuantity] = useState(1);
  const [addGiftedBalance, setAddGiftedBalance] = useState(false);
  const giftedValueRef = useRef(null);
  const [draftGiftedValue, setDraftGiftedValue] = useState(0);
  const [expirationDate, setExpirationDate] = useState(dayjs().add(3, 'year').format('YYYY-MM-DD'));
  const [validationErrors, setValidationErrors] = useState({});

  const additionalCommentRef = useRef(null);
  const bankBookingDateRef = useRef(null);
  const bankReferenceRef = useRef(null);

  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  const commentPrefix = `Buchhaltung: Zahlungseingang (Banküberweisung) bestätigt durch ${
    authCtx.currentUser?.userName || 'Mitarbeiter'
  }`;

  const getFullComment = () => {
    const paymentMeta = [];
    const bookingDate = bankBookingDateRef.current?.value?.trim();
    const reference = bankReferenceRef.current?.value?.trim();

    if (bookingDate) paymentMeta.push(`Buchungsdatum ${bookingDate}`);
    if (reference) paymentMeta.push(`Verwendungszweck/Ref. ${reference}`);

    const suffix = paymentMeta.length ? ` (${paymentMeta.join(', ')})` : '';

    const additionalComment = additionalCommentRef.current?.value?.trim() || '';
    const base = `${commentPrefix}${suffix}`;
    return additionalComment ? `${base}: ${additionalComment}` : base;
  };

  const clearValidationError = key => {
    setValidationErrors(prev => {
      const next = { ...(prev || {}) };
      delete next[key];
      return next;
    });
  };

  const [createdVoucher, setCreatedVoucher] = useState(null);
  const [createdVouchers, setCreatedVouchers] = useState([]);
  const [zipUrl, setZipUrl] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [sentEmailAddress, setSentEmailAddress] = useState('');
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalVoucher, setEmailModalVoucher] = useState(null);
  const [emailModalSending, setEmailModalSending] = useState(false);
  const [selectedListVoucher, setSelectedListVoucher] = useState(null);
  const [selectedListVoucherPdfUrl, setSelectedListVoucherPdfUrl] = useState(null);
  const [recentVouchers, setRecentVouchers] = useState([]);
  const [recentVouchersLoading, setRecentVouchersLoading] = useState(false);

  const emailInputRef = useRef(null);
  const listEmailInputRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [valueProducts, resourceProducts] = await Promise.all([
          getAllVoucherProducts(),
          getAllResourceVoucherProducts(),
        ]);
        setVoucherProducts(valueProducts || []);
        // Goodwill (Kulanz) products are issued via free-game flextickets only
        // and must not be offered for sale.
        setResourceVoucherProducts(
          (Array.isArray(resourceProducts) ? resourceProducts : []).filter(
            product => product.isGoodwillProduct !== true
          )
        );
      } catch (error) {
        toast.error('Fehler beim Laden der Gutscheinprodukte');
        console.error('Error fetching voucher products:', error);
      }
    };
    fetchProducts();
  }, []);

  const fetchRecentVouchers = async () => {
    setRecentVouchersLoading(true);
    try {
      const yesterday = dayjs().subtract(1, 'day').startOf('day').toISOString();
      const filters = JSON.stringify([{ field: 'createdAt', operator: 'gte', value: yesterday }]);
      const response = await getAllVouchers('', 1, 50, [], true, filters);
      setRecentVouchers(response.results || []);
    } catch (error) {
      console.error('Error fetching recent vouchers:', error);
    } finally {
      setRecentVouchersLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentVouchers();
  }, []);

  const selectedProduct = useMemo(() => {
    if (selectedProductType === 'resource') {
      return resourceVoucherProducts.find(product => product._id === selectedVoucherProduct);
    }
    return voucherProducts.find(product => product._id === selectedVoucherProduct);
  }, [voucherProducts, resourceVoucherProducts, selectedVoucherProduct, selectedProductType]);

  const isResourceVoucher = selectedProductType === 'resource';

  const handleVoucherProductChange = (productId, type) => {
    setSelectedVoucherProduct(productId);
    setSelectedProductType(type);
    setSelectedSuggestedValue('');
    setVoucherValue('');

    if (productId && type === 'value') {
      const product = voucherProducts.find(p => p._id === productId);
      if (
        product &&
        product.defaultValue &&
        (!product.suggestedValues || product.suggestedValues.length === 0)
      ) {
        setVoucherValue(product.defaultValue.toString());
      }
    }
  };

  const handleSuggestedValueChange = value => {
    setSelectedSuggestedValue(value);
    if (value === 'custom') {
      setVoucherValue('');
    } else {
      setVoucherValue(value);
    }
  };

  useEffect(() => {
    if (
      !isResourceVoucher &&
      selectedProduct &&
      selectedProduct.suggestedValues &&
      selectedProduct.suggestedValues.length > 0
    ) {
      if (
        selectedProduct.defaultValue &&
        selectedProduct.suggestedValues.includes(selectedProduct.defaultValue)
      ) {
        setSelectedSuggestedValue(selectedProduct.defaultValue.toString());
        setVoucherValue(selectedProduct.defaultValue.toString());
      } else if (selectedProduct.suggestedValues.length > 0) {
        const firstValue = selectedProduct.suggestedValues[0];
        setSelectedSuggestedValue(firstValue.toString());
        setVoucherValue(firstValue.toString());
      }
    }
  }, [selectedProduct, isResourceVoucher]);

  const validateStep1 = () => {
    const errors = {};

    if (!selectedVoucherProduct) {
      errors.selectedVoucherProduct = 'Bitte wähle ein Produkt aus';
    }

    const quantity = parseInt(String(quantityRef.current?.value || '1'), 10) || 1;
    setDraftQuantity(quantity);
    if (quantity < 1 || quantity > 200) {
      errors.quantity = 'Quantität muss zwischen 1 und 200 liegen';
    }

    if (!isResourceVoucher) {
      const paid = parseFloat(voucherValue || '0') || 0;
      const gifted = addGiftedBalance
        ? parseFloat(String(giftedValueRef.current?.value || '0').replace(',', '.')) || 0
        : 0;
      setDraftGiftedValue(gifted);

      if (paid <= 0) {
        errors.voucherValue = 'Wert muss größer als 0 sein';
      }

      if (addGiftedBalance && gifted <= 0) {
        errors.giftedValue = 'Geschenktes Guthaben muss größer als 0 sein';
      } else if (gifted < 0) {
        errors.giftedValue = 'Geschenktes Guthaben darf nicht negativ sein';
      }

      const total = paid + gifted;
      if (!errors.voucherValue && total > 2000) {
        errors.voucherValue = 'Gesamtwert darf maximal 2000€ betragen';
      } else if (selectedProduct) {
        if (selectedProduct.minValue && total < selectedProduct.minValue) {
          errors.voucherValue = `Wert muss mindestens ${selectedProduct.minValue.toFixed(2)}€ betragen`;
        } else if (selectedProduct.maxValue && total > selectedProduct.maxValue) {
          errors.voucherValue = `Wert darf maximal ${selectedProduct.maxValue.toFixed(2)}€ betragen`;
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};

    if (!paymentConfirmed) {
      errors.paymentConfirmed = 'Bitte bestätige den Zahlungseingang';
    }

    setValidationErrors(errors);
    if (errors.paymentConfirmed) toast.error('Bitte Zahlungseingang per Banküberweisung bestätigen');
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (validateStep1()) {
        setActiveStep(1);
      }
    } else if (activeStep === 1) {
      if (validateStep2()) {
        await createVoucher();
      }
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setValidationErrors({});
  };

  const createVoucher = async () => {
    setIsCreating(true);
    try {
      let result;
      const quantity = draftQuantity > 0 ? draftQuantity : 1;
      if (isResourceVoucher) {
        result = await sellOfflineResourceVoucher({
          resourceVoucherProductId: selectedVoucherProduct,
          expirationDate,
          creationComment: getFullComment(),
          quantity,
          posConfirmed: true,
        });
        result.isResourceVoucher = true;
      } else {
        const gifted = addGiftedBalance ? Number(draftGiftedValue || 0) || 0 : 0;
        result = await sellOfflineVoucher({
          voucherValue: parseFloat(voucherValue),
          giftedValue: gifted,
          expirationDate,
          creationComment: getFullComment(),
          relatedVoucherProductId: selectedVoucherProduct,
          quantity,
          posConfirmed: true,
        });
        result.isResourceVoucher = false;
      }

      if (Array.isArray(result?.vouchers) && result.vouchers.length > 0) {
        setCreatedVouchers(result.vouchers);
        setCreatedVoucher(result.vouchers[0]);
        if (!isResourceVoucher) {
          try {
            const ids = result.vouchers.map(v => v.voucherId).filter(Boolean);
            if (ids.length > 1) {
              const zip = await getVouchersZipURL(ids);
              setZipUrl(zip?.preSignedURL || null);
            }
          } catch (_) {
            setZipUrl(null);
          }
        }
      } else {
        setCreatedVouchers([]);
        setCreatedVoucher(result);
      }
      setActiveStep(2);
      toast.success(
        Array.isArray(result?.vouchers) && result.vouchers.length > 1
          ? `${result.vouchers.length} Gutscheine erfolgreich erstellt!`
          : 'Gutschein erfolgreich erstellt!'
      );
      fetchRecentVouchers();
    } catch (error) {
      toast.error('Fehler beim Erstellen des Gutscheins');
      console.error('Error creating voucher:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenPdfModal = () => {
    setSelectedListVoucher(null);
    setSelectedListVoucherPdfUrl(null);
    setPdfViewerOpen(true);
  };

  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    setSelectedListVoucher(null);
    setSelectedListVoucherPdfUrl(null);
  };

  const handleOpenInNewTab = () => {
    if (createdVoucher?.preSignedURL) {
      window.open(inlinePdfUrl(createdVoucher.preSignedURL), '_blank');
    }
  };

  const handleOpenListVoucherPdf = async voucher => {
    try {
      const response = await getVoucherDownloadURL(voucher._id);
      if (response?.preSignedURL) {
        const url = response.preSignedURL;
        const inlineUrl = `${url}${url.includes('?') ? '&' : '?'}disposition=inline`;
        setSelectedListVoucher(voucher);
        setSelectedListVoucherPdfUrl(inlineUrl);
        setPdfViewerOpen(true);
      }
    } catch (error) {
      toast.error('Fehler beim Laden des Gutscheins');
      console.error('Error loading voucher PDF:', error);
    }
  };

  const handleSendEmail = async () => {
    const email = emailInputRef.current?.value?.trim();
    if (!isValidEmail(email)) {
      toast.error('Bitte gib eine gültige E-Mail-Adresse ein');
      return;
    }
    if (createdVoucher?.isResourceVoucher) {
      toast.error('E-Mail-Versand ist für Produktgutscheine hier nicht verfügbar');
      return;
    }

    setEmailSending(true);
    try {
      if (createdVouchers.length > 0) {
        for (const v of createdVouchers) {
          if (!v?.voucherId) continue;
          await sendVoucherToEmail(v.voucherId, email);
        }
      } else {
        await sendVoucherToEmail(createdVoucher.voucherId, email);
      }
      setSentEmailAddress(email);
      toast.success('E-Mail erfolgreich gesendet!');
    } catch (error) {
      toast.error('Fehler beim Senden der E-Mail');
      console.error('Error sending email:', error);
    } finally {
      setEmailSending(false);
    }
  };

  const handleOpenEmailModal = voucher => {
    setEmailModalVoucher(voucher);
    setEmailModalOpen(true);
  };

  const handleCloseEmailModal = () => {
    setEmailModalOpen(false);
    setEmailModalVoucher(null);
    if (listEmailInputRef.current) listEmailInputRef.current.value = '';
  };

  const handleSendListEmail = async () => {
    const email = listEmailInputRef.current?.value?.trim();
    if (!isValidEmail(email)) {
      toast.error('Bitte gib eine gültige E-Mail-Adresse ein');
      return;
    }

    setEmailModalSending(true);
    try {
      await sendVoucherToEmail(emailModalVoucher._id, email);
      toast.success('E-Mail erfolgreich gesendet!');
      handleCloseEmailModal();
    } catch (error) {
      toast.error('Fehler beim Senden der E-Mail');
      console.error('Error sending email:', error);
    } finally {
      setEmailModalSending(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedVoucherProduct('');
    setSelectedProductType('');
    setSelectedSuggestedValue('');
    setVoucherValue('');
    setExpirationDate(dayjs().add(3, 'year').format('YYYY-MM-DD'));
    if (additionalCommentRef.current) additionalCommentRef.current.value = '';
    if (bankBookingDateRef.current) bankBookingDateRef.current.value = dayjs().format('YYYY-MM-DD');
    if (bankReferenceRef.current) bankReferenceRef.current.value = '';
    setPaymentConfirmed(false);
    setValidationErrors({});
    setCreatedVoucher(null);
    if (emailInputRef.current) emailInputRef.current.value = '';
    setSentEmailAddress('');
  };

  const renderStep1 = () => (
    <Fade in={activeStep === 0} timeout={300}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <CardGiftcardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Gutschein konfigurieren
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Wähle das Produkt und den Wert für den Gutschein
            </Typography>
          </Box>
        </Box>

        <Stack spacing={3}>
          <FormControl fullWidth error={!!validationErrors.selectedVoucherProduct}>
            <InputLabel>Gutscheinprodukt *</InputLabel>
            <Select
              value={selectedVoucherProduct}
              onChange={e => {
                const id = e.target.value;
                const isResource = resourceVoucherProducts.some(p => p._id === id);
                handleVoucherProductChange(id, isResource ? 'resource' : 'value');
              }}
              label="Gutscheinprodukt"
              disabled={isLoadingProducts || isLoadingResourceProducts}
            >
              {voucherProducts.length > 0 && <ListSubheader>Wertgutscheine</ListSubheader>}
              {voucherProducts.map(product => (
                <MenuItem key={product._id} value={product._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {product.name}
                    {product.branchId && (
                      <Chip
                        label={product.branchId.longName}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
              {resourceVoucherProducts.length > 0 && <ListSubheader>Produktgutscheine</ListSubheader>}
              {resourceVoucherProducts.map(product => (
                <MenuItem key={product._id} value={product._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {product.name}
                    <Chip
                      label={`${Math.round(product.durationMS / 60000)} Min`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                    <Chip label={`${product.personCount} Pers.`} size="small" variant="outlined" />
                    <Chip
                      label={`${(product.basePrice || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
                      size="small"
                      color="primary"
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {validationErrors.selectedVoucherProduct && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {validationErrors.selectedVoucherProduct}
              </Typography>
            )}
          </FormControl>

          {!isResourceVoucher && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {selectedProduct &&
                selectedProduct.suggestedValues &&
                selectedProduct.suggestedValues.length > 0 && (
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Vorgeschlagene Werte</InputLabel>
                    <Select
                      value={selectedSuggestedValue}
                      onChange={e => handleSuggestedValueChange(e.target.value)}
                      label="Vorgeschlagene Werte"
                    >
                      {selectedProduct.suggestedValues.map(value => (
                        <MenuItem key={value} value={value.toString()}>
                          {value.toFixed(2)} €
                        </MenuItem>
                      ))}
                      {selectedProduct.allowCustomValue && (
                        <MenuItem value="custom">
                          <em>Eigener Wert</em>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}

              {(!selectedProduct ||
                !selectedProduct.suggestedValues ||
                selectedProduct.suggestedValues.length === 0 ||
                selectedSuggestedValue === 'custom') && (
                <TextField
                  label="Gutscheinwert *"
                  type="number"
                  value={voucherValue}
                  onChange={e => setVoucherValue(e.target.value)}
                  error={!!validationErrors.voucherValue}
                  helperText={validationErrors.voucherValue}
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  inputProps={{
                    min: selectedProduct?.minValue || 0.01,
                    max: selectedProduct?.maxValue || 2000,
                    step: 0.01,
                  }}
                />
              )}
            </Box>
          )}

          {!isResourceVoucher && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                px: 2,
                py: 1.5,
                backgroundColor: theme => theme.palette.action.hover,
              }}
            >
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={addGiftedBalance}
                    onChange={e => {
                      setAddGiftedBalance(e.target.checked);
                      if (!e.target.checked) {
                        setDraftGiftedValue(0);
                        if (giftedValueRef.current) giftedValueRef.current.value = '0';
                        clearValidationError('giftedValue');
                      }
                    }}
                  />
                }
                label='Geschenktes Guthaben hinzufügen'
              />
              <Collapse in={addGiftedBalance} timeout='auto' unmountOnExit>
                <Box sx={{ pt: 1, pl: { xs: 0, sm: 4 } }}>
                  <TextField
                    fullWidth
                    sx={{ maxWidth: 420 }}
                    inputRef={giftedValueRef}
                    defaultValue={String(draftGiftedValue || 0)}
                    label='Geschenktes Guthaben'
                    type='number'
                    onBlur={() => {
                      const value =
                        parseFloat(String(giftedValueRef.current?.value || '0').replace(',', '.')) || 0;
                      setDraftGiftedValue(value);
                      if (giftedValueRef.current) giftedValueRef.current.value = String(value);
                      if (value > 0) clearValidationError('giftedValue');
                    }}
                    error={!!validationErrors.giftedValue}
                    helperText={
                      validationErrors.giftedValue ||
                      'Wird zusätzlich zum bezahlten Guthaben als Geschenk gebucht'
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position='start'>€</InputAdornment>,
                    }}
                    inputProps={{ min: 0.01, max: 2000, step: 0.01 }}
                  />
                </Box>
              </Collapse>
            </Box>
          )}

          <TextField
            inputRef={quantityRef}
            defaultValue={String(draftQuantity || 1)}
            label="Quantität *"
            type="number"
            onBlur={() => {
              const value = parseInt(String(quantityRef.current?.value || '1'), 10) || 1;
              setDraftQuantity(value);
              if (quantityRef.current) quantityRef.current.value = String(value);
              if (value >= 1 && value <= 200) clearValidationError('quantity');
            }}
            error={!!validationErrors.quantity}
            helperText={validationErrors.quantity || 'Anzahl der zu erstellenden Gutscheine (1–200)'}
            inputProps={{ min: 1, max: 200, step: 1 }}
          />

          {selectedProduct && !isResourceVoucher && (
            <Alert severity="info" icon={<LocalAtmIcon />}>
              <Typography variant="body2">
                <strong>Gesamtwert:</strong>{' '}
                {(
                  Number(parseFloat(voucherValue || 0) || 0) + (addGiftedBalance ? Number(draftGiftedValue || 0) : 0)
                ).toLocaleString('de-DE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                €{' '}
                {addGiftedBalance ? (
                  <span style={{ opacity: 0.85 }}>
                    (bezahlt{' '}
                    {Number(parseFloat(voucherValue || 0) || 0).toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    € + geschenkt{' '}
                    {Number(draftGiftedValue || 0).toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    €)
                  </span>
                ) : null}
              </Typography>
            </Alert>
          )}

          {selectedProduct && isResourceVoucher && (
            <Alert severity="info" icon={<CardGiftcardIcon />}>
              <Typography variant="body2">
                <strong>Produktgutschein:</strong> {selectedProduct.name} -{' '}
                {Math.round(selectedProduct.durationMS / 60000)} Minuten für{' '}
                {selectedProduct.personCount} Personen -{' '}
                <strong>
                  {(selectedProduct.basePrice || 0).toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{' '}
                  €
                </strong>
              </Typography>
            </Alert>
          )}

          <Box>
            <Typography variant="body2" color="text.secondary">
              Ablaufdatum
            </Typography>
            <Typography variant="body1">
              {dayjs(expirationDate).format('DD.MM.YYYY')} (3 Jahre)
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Kommentar
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {commentPrefix}
            </Typography>
            <TextField
              placeholder="Zusätzlicher Kommentar (optional)"
              multiline
              rows={2}
              inputRef={additionalCommentRef}
              fullWidth
              size="small"
            />
          </Box>
        </Stack>
      </Box>
    </Fade>
  );

  const renderStep2 = () => (
    <Fade in={activeStep === 1} timeout={300}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <AccountBalanceIcon sx={{ fontSize: 40, color: 'warning.main' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Zahlungseingang bestätigen
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bestätige den Zahlungseingang per Banküberweisung
            </Typography>
          </Box>
        </Box>

        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Zusammenfassung
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Produkt:</Typography>
                <Typography>{selectedProduct?.name}</Typography>
              </Box>
              {isResourceVoucher ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Dauer:</Typography>
                    <Typography fontWeight="bold">
                      {Math.round(selectedProduct?.durationMS / 60000)} Minuten
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Personen:</Typography>
                    <Typography fontWeight="bold">{selectedProduct?.personCount}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Preis:</Typography>
                    <Typography fontWeight="bold">
                      {(selectedProduct?.basePrice || 0).toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      €
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Bezahlt:</Typography>
                    <Typography fontWeight="bold">
                      {parseFloat(voucherValue || 0).toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      €
                    </Typography>
                  </Box>
                  {addGiftedBalance && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Geschenkt:</Typography>
                      <Typography fontWeight="bold">
                        {Number(draftGiftedValue || 0).toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        €
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Gesamtwert:</Typography>
                    <Typography fontWeight="bold">
                      {(Number(parseFloat(voucherValue || 0) || 0) + Number(draftGiftedValue || 0)).toLocaleString(
                        'de-DE',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      )}{' '}
                      €
                    </Typography>
                  </Box>
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Quantität:</Typography>
                <Typography fontWeight="bold">{draftQuantity || 1}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Gültig bis:</Typography>
                <Typography>{dayjs(expirationDate).format('DD.MM.YYYY')}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Alert
          severity="warning"
          sx={{
            mb: 3,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Typography variant="body1" fontWeight="medium" gutterBottom>
            Wichtiger Hinweis:
          </Typography>
          <Typography variant="body2">
            Bitte prüfe den Kontoauszug und bestätige den Zahlungseingang über{' '}
            <strong>
              {isResourceVoucher
                ? (selectedProduct?.basePrice || 0).toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : parseFloat(voucherValue || 0).toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{' '}
              €
            </strong>{' '}
            bevor du fortfährst.
          </Typography>
        </Alert>

        <Stack spacing={2} sx={{ maxWidth: 520, mx: 'auto' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={paymentConfirmed}
                onChange={e => {
                  setPaymentConfirmed(e.target.checked);
                  if (e.target.checked) clearValidationError('paymentConfirmed');
                }}
              />
            }
            label="Zahlungseingang per Banküberweisung ist eingegangen"
          />
          {validationErrors.paymentConfirmed && (
            <Typography variant="caption" color="error" sx={{ mt: -1 }}>
              {validationErrors.paymentConfirmed}
            </Typography>
          )}

          <TextField
            inputRef={bankBookingDateRef}
            defaultValue={dayjs().format('YYYY-MM-DD')}
            label="Buchungsdatum (optional)"
            type="date"
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            inputRef={bankReferenceRef}
            label="Verwendungszweck / Referenz (optional)"
            fullWidth
            size="small"
            placeholder="z.B. Überweisungs-Referenz"
          />
        </Stack>
      </Box>
    </Fade>
  );

  const renderStep3 = () => (
    <Fade in={activeStep === 2} timeout={300}>
      <Box sx={{ textAlign: 'center' }}>
        <Grow in={activeStep === 2} timeout={500}>
          <Box>
            <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Gutschein erstellt!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Der Gutschein wurde erfolgreich angelegt und ist sofort gültig.
            </Typography>
          </Box>
        </Grow>

        <Card
          elevation={3}
          sx={{
            maxWidth: 400,
            mx: 'auto',
            mb: 4,
            backgroundColor: theme.palette.primary.main,
            backgroundImage: 'none',
            color: theme.palette.primary.contrastText,
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ py: 4 }}>
            <CardGiftcardIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
            {createdVoucher?.isResourceVoucher ? (
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {selectedProduct?.name}
              </Typography>
            ) : (
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {(Number(parseFloat(voucherValue || 0) || 0) + Number(draftGiftedValue || 0)).toLocaleString('de-DE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                €
              </Typography>
            )}
            <Typography variant="h6" sx={{ opacity: 0.9, letterSpacing: 2 }}>
              {createdVouchers.length > 1 ? `${createdVouchers.length} Gutscheine` : createdVoucher?.voucherCode}
            </Typography>
            <Divider
              sx={{
                my: 2,
                borderColor: alpha(theme.palette.primary.contrastText, 0.3),
              }}
            />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Gültig bis {dayjs(expirationDate).format('DD.MM.YYYY')}
            </Typography>
          </CardContent>
        </Card>

        {createdVouchers.length > 1 && (
          <Box sx={{ maxWidth: 720, mx: 'auto', mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Erstellte Gutscheine
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {createdVouchers.map(v => (
                    <TableRow key={v.voucherId || v.voucherCode}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {v.voucherCode}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          sx={{ mb: 4 }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<PrintIcon />}
            onClick={handleOpenPdfModal}
            sx={{ textTransform: 'none' }}
          >
            PDF öffnen & drucken
          </Button>
          {zipUrl && (
            <Button
              variant="contained"
              size="large"
              onClick={() => window.open(zipUrl, '_blank')}
              sx={{ textTransform: 'none' }}
            >
              ZIP (alle PDFs)
            </Button>
          )}
          <Button
            variant="outlined"
            size="large"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
            sx={{ textTransform: 'none' }}
          >
            In neuem Tab öffnen
          </Button>
        </Stack>

        <Divider sx={{ my: 4 }}>
          <Chip label="Optional: E-Mail an Kunden senden" />
        </Divider>

        <Box sx={{ maxWidth: 500, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <EmailIcon color="primary" />
            <Typography variant="body1" fontWeight="medium">
              Gutschein per E-Mail versenden
            </Typography>
          </Box>

          {sentEmailAddress ? (
            <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
              E-Mail wurde erfolgreich an <strong>{sentEmailAddress}</strong> gesendet!
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                inputRef={emailInputRef}
                label="E-Mail-Adresse des Kunden"
                type="email"
                fullWidth
                size="small"
                placeholder="kunde@example.com"
              />
              <Button
                variant="contained"
                onClick={handleSendEmail}
                disabled={emailSending}
                startIcon={emailSending ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{ minWidth: 120 }}
              >
                {emailSending ? 'Senden...' : 'Senden'}
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Button
            variant="text"
            size="large"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            sx={{ textTransform: 'none' }}
          >
            Weiteren Zahlungseingang bestätigen
          </Button>
        </Box>
      </Box>
    </Fade>
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 2, md: 4 }, maxWidth: 800, mx: 'auto' }}>
      <Stepper activeStep={activeStep} sx={{ mb: 5 }} alternativeLabel>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  '&.Mui-completed': { color: 'success.main' },
                  '&.Mui-active': { color: 'primary.main' },
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 400 }}>
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
      </Box>

      {activeStep < 2 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: activeStep === 0 ? 'flex-end' : 'space-between',
            mt: 4,
          }}
        >
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ textTransform: 'none' }}>
              Zurück
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isCreating}
            startIcon={isCreating ? <CircularProgress size={20} /> : null}
            sx={{ textTransform: 'none' }}
          >
            {isCreating ? 'Wird erstellt...' : activeStep === 1 ? 'Gutschein erstellen' : 'Weiter'}
          </Button>
        </Box>
      )}

      <Accordion sx={{ mt: 6 }} defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight="medium">Zuletzt von mir erstellte Gutscheine</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {recentVouchersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : recentVouchers.length === 0 ? (
            <Typography color="text.secondary">
              Keine Gutscheine in den letzten 2 Tagen erstellt.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Wert</TableCell>
                    <TableCell>Erstellt am</TableCell>
                    <TableCell>Kommentar</TableCell>
                    <TableCell align="right">Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentVouchers.map(voucher => (
                    <TableRow key={voucher._id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {voucher.voucherCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {voucher.initialBalance?.sum?.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        €
                      </TableCell>
                      <TableCell>{dayjs(voucher.meta?.createdAt).format('DD.MM.YYYY HH:mm')}</TableCell>
                      <TableCell>
                        <Tooltip title={voucher.creationComment || '-'} arrow>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 200, cursor: 'default' }}
                          >
                            {voucher.creationComment || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenListVoucherPdf(voucher)}
                          title="PDF anzeigen"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEmailModal(voucher)}
                          title="Per E-Mail senden"
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '85vh' } }}
      >
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Gutschein {selectedListVoucher?.voucherCode || createdVoucher?.voucherCode}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleClosePdfViewer}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 0, height: 'calc(85vh - 64px)' }}>
          {(selectedListVoucherPdfUrl || createdVoucher?.preSignedURL) && (
            <iframe
              src={(() => {
                const url = selectedListVoucherPdfUrl || createdVoucher?.preSignedURL;
                const inlineUrl = url.includes('disposition=inline')
                  ? url
                  : `${url}${url.includes('?') ? '&' : '?'}disposition=inline`;
                return `${inlineUrl}#toolbar=0&navpanes=0`;
              })()}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Gutschein PDF"
            />
          )}
        </Box>
      </Dialog>

      <Dialog open={emailModalOpen} onClose={handleCloseEmailModal} maxWidth="sm" fullWidth>
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Gutschein per E-Mail senden
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseEmailModal}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Gutschein <strong>{emailModalVoucher?.voucherCode}</strong> an folgende E-Mail-Adresse senden:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              inputRef={listEmailInputRef}
              label="E-Mail-Adresse"
              type="email"
              fullWidth
              size="small"
              placeholder="kunde@example.com"
              autoFocus
            />
            <Button
              variant="contained"
              onClick={handleSendListEmail}
              disabled={emailModalSending}
              startIcon={emailModalSending ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ minWidth: 120 }}
            >
              {emailModalSending ? 'Senden...' : 'Senden'}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default ConfirmBankTransferVoucherPayment;

